import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Copy, Upload, X, Check } from "lucide-react";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { collection, doc, setDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../Config/config";

const PurchaseScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { packageDetails, personalDetails } = location.state || {};

  const [screenshot, setScreenshot] = useState(null);
  const [ssUrl, setSsUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);

  const currentDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const copyToClipboard = (text, message) => {
    navigator.clipboard.writeText(text).then(() => {
      alert(message);
    });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setScreenshot(file);
    }
  };

  const uploadImageToFirebase = async () => {
    if (screenshot) {
      setUploading(true);
      const storage = getStorage();
      const storageRef = ref(storage, "payments/" + new Date().toISOString());

      try {
        await uploadBytes(storageRef, screenshot);
        const downloadURL = await getDownloadURL(storageRef);
        setSsUrl(downloadURL);
      } catch (error) {
        console.error("Error uploading image: ", error);
        alert("Failed to upload image. Please try again.");
      } finally {
        setUploading(false);
      }
    }
  };

  const checkout = async () => {
    setProcessing(true);
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      alert("You must be logged in to make a purchase.");
      setProcessing(false);
      return;
    }

    const purchaseRef = doc(collection(db, "users", user.uid, "purchases"));

    try {
      await setDoc(purchaseRef, {
        packageName: packageDetails.name,
        duration: packageDetails.duration,
        purchaseId: purchaseRef.id,
        purchaseDate: currentDate,
        properPurchaseDate: new Date(),
        packageType: packageDetails.type,
        status: "pending",
        ssUrl: ssUrl,
        price: personalDetails.totalPrice,
        people: personalDetails.people,
        nominees: personalDetails.nominees,
        adults: personalDetails.adults,
        kids: personalDetails.kids,
        maxMembers: personalDetails.maxMembers, 
        locations: personalDetails.selectedLocations,
        name: user.displayName,
        userId: user.uid
      });
      navigate("/payment-done");
    } catch (error) {
      console.error("Error during checkout:", error);
      alert("An error occurred during checkout. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  if (processing) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingText>Processing your purchase...</LoadingText>
      </LoadingContainer>
    );
  }

  return (
    <Container>
      <ScrollContainer>
        <MainContainer>
          {/* Payment Summary */}
          <SummaryCard>
            <CardTitle>Payment Summary</CardTitle>
            <SummaryItem>
              <Label>Package Name</Label>
              <Value>{packageDetails.name}</Value>
            </SummaryItem>
            <SummaryItem>
              <Label>Total Members</Label>
              <Value>{personalDetails.maxMembers}</Value>
            </SummaryItem>
            <SummaryItem>
              <Label>Base Package Price</Label>
              <Value>₹{packageDetails.price}</Value>
            </SummaryItem>
            <SummaryItem>
              <Label>Additional Adults ({personalDetails.adults - 2})</Label>
              <Value>
                ₹
                {(
                  (personalDetails.adults - 2) *
                  packageDetails.price *
                  0.5
                ).toFixed(2)}
              </Value>
            </SummaryItem>
            <SummaryItem>
              <Label>Additional Kids ({personalDetails.kids - 1})</Label>
              <Value>
                ₹
                {(
                  (personalDetails.kids - 1) *
                  packageDetails.price *
                  0.25
                ).toFixed(2)}
              </Value>
            </SummaryItem>
            <TotalAmount>
              <Label>Total Amount</Label>
              <Value>₹{personalDetails.totalPrice}</Value>
            </TotalAmount>
          </SummaryCard>

          {/* Bank Details */}
          <MainContainer>
            <CardTitle>Bank Account Details</CardTitle>
            <CopyableField>
              <Text>
                Account No: <strong>119763700000143</strong>
              </Text>
              <CopyButton
                onClick={() =>
                  copyToClipboard("119763700000143", "Account Number Copied")
                }
              >
                <Copy size={20} />
              </CopyButton>
            </CopyableField>
            <CopyableField>
              <Text>
                Bank Name : <strong>YES Bank</strong>
              </Text>
              <CopyButton
                onClick={() => copyToClipboard("YES Bank", "Bank Name Copied")}
              >
                <Copy size={20} />
              </CopyButton>
            </CopyableField>
            <CopyableField>
              <Text>
                IFSC Code : <strong>YESB0001197</strong>
              </Text>
              <CopyButton
                onClick={() => copyToClipboard("YESB0001197", "IFSC Copied")}
              >
                <Copy size={20} />
              </CopyButton>
            </CopyableField>
            <CopyableField>
              <Text>
                Account Holder Name : <strong>Joysure</strong>
              </Text>
              <CopyButton
                onClick={() =>
                  copyToClipboard("Joysure", "Account Holder Name Copied")
                }
              >
                <Copy size={20} />
              </CopyButton>
            </CopyableField>
            <CopyableField>
              <Text>
                Branch : <strong>Lokhandwala Branch</strong>
              </Text>
              <CopyButton
                onClick={() =>
                  copyToClipboard("Lokhandwala Branch", "Branch Name Copied")
                }
              >
                <Copy size={20} />
              </CopyButton>
            </CopyableField>
          </MainContainer>

          {/* Screenshot Upload */}
          <ScreenshotSection>
            <ScreenshotTitle>Payment Screenshot</ScreenshotTitle>
            {uploading ? (
              <LoadingSpinner />
            ) : screenshot ? (
              <ScreenshotPreview
                src={URL.createObjectURL(screenshot)}
                alt="Screenshot preview"
              />
            ) : null}
            {screenshot && !ssUrl ? (
              <ButtonGroup>
                <CancelButton onClick={() => setScreenshot(null)}>
                  <X size={20} />
                  CANCEL
                </CancelButton>
                <UploadButton onClick={uploadImageToFirebase}>
                  <Upload size={20} />
                  UPLOAD
                </UploadButton>
              </ButtonGroup>
            ) : screenshot && ssUrl ? (
              <CheckoutButton onClick={checkout}>
                <Check size={20} />
                COMPLETE PURCHASE
              </CheckoutButton>
            ) : (
              <UploadButton as="label">
                <Upload size={20} />
                Upload Screenshot
                <input
                  type="file"
                  hidden
                  onChange={handleFileChange}
                  accept="image/*"
                />
              </UploadButton>
            )}
          </ScreenshotSection>
        </MainContainer>
      </ScrollContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding: 20px;
  background-color: #f5f5f5;
  margin: 15vh 0 0 0;
`;

const ScrollContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
`;

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SummaryCard = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const CardTitle = styled.h2`
  font-family: "Poppins-SemiBold", sans-serif;
  color: #441B77;
  font-size: 20px;
  margin-bottom: 16px;
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
`;

const Label = styled.span`
  font-family: "Poppins-Medium", sans-serif;
  color: #666;
`;

const Value = styled.span`
  font-family: "Poppins-SemiBold", sans-serif;
  color: #333;
`;

const TotalAmount = styled(SummaryItem)`
  margin-top: 16px;
  border-bottom: none;
  padding-top: 16px;
  border-top: 2px solid #eee;

  ${Label}, ${Value} {
    font-size: 18px;
    color: #441B77;
  }
`;

const CopyableField = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background-color: white;
  border-radius: 6px;
  margin-bottom: 8px;
`;

const Text = styled.p`
  font-family: "Poppins-Regular", sans-serif;
  color: #333;
  margin: 0;

  strong {
    font-family: "Poppins-SemiBold", sans-serif;
    color: #441B77;
  }
`;

const CopyButton = styled.button`
  background: none;
  border: none;
  color: #441B77;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    color: #441B77;
  }
`;

const ScreenshotSection = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const ScreenshotTitle = styled.h3`
  font-family: "Poppins-SemiBold", sans-serif;
  color: #441B77;
  margin-bottom: 16px;
`;

const ScreenshotPreview = styled.img`
  max-width: 100%;
  height: auto;
  margin: 16px 0;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 16px;
`;

const Button = styled.button`
  flex: 1;
  padding: 12px;
  border-radius: 6px;
  border: none;
  font-family: "Poppins-SemiBold", sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s;
`;

const UploadButton = styled(Button)`
  background-color: #441B77;
  color: white;

  &:hover {
    background-color: #441B77;
  }
`;

const CancelButton = styled(Button)`
  background-color: #dc3545;
  color: white;

  &:hover {
    background-color: #c82333;
  }
`;

const CheckoutButton = styled(Button)`
  background-color: #28a745;
  color: white;

  &:hover {
    background-color: #218838;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f5f5f5;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #441B77;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.p`
  font-family: "Poppins-Medium", sans-serif;
  color: #441B77;
  margin-top: 16px;
`;

export default PurchaseScreen;